import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled, { withTheme } from "styled-components";
import { appStudy } from "../../../config";
import {
  DateFormat,
  getBaseDateFormat,
  REPORT_TYPES,
} from "../../../modules/constants";
import { getDates, pdfDownload } from "../../../modules/utils";
import AnimatedButton from "../../ui-library/Buttons/AnimatedButton/AnimatedButton";
import CustomDropdown from "../../ui-library/CustomDropdown/CustomDropdown";
import DateSelect from "../../ui-library/DateAndTime/Date/DateSelect";
import {
  getDataTo24HRecallReport,
  headers24hRecall,
  parseDailyTargetHistoryData,
} from "../../reports/csv/24hRecall";
import { exportCSVFile } from "../../reports/csv/helpers";
import { useTranslation } from "react-i18next";
import ToleratedDoseStudyDrugReport from "../../reports/pdf/ToleratedDoseStudyDrugReport/ToleratedDoseStudyDrugReport";
import StudyDrugIntakeReport from "../../reports/pdf/StudyDrugIntakeReport/StudyDrugIntakeReport";
import {
  getDataToToleratedDoseStudyDrugReport,
  getPastDate,
} from "../../reports/pdf/ToleratedDoseStudyDrugReport/utils";
import { getDataToStudyDrugIntakeReport } from "../../reports/pdf/StudyDrugIntakeReport/utils";
import {
  getDataToDietRecordSummaryReport,
  headersDietRecordSummary,
} from "../../reports/csv/dietRecordSummary";
import DailyAveragesReport from "../../reports/pdf/DailyAveragesReport/DailyAveragesReport";
import { getDataToDailyAveragesReport } from "../../reports/pdf/DailyAveragesReport/utils";
import { generateWeeks } from "../../../helpers/mealsHelper";
import AlertUGNX from "../../ToastsAndAlerts/AlertUGNX";
import { AlertType } from "../../ToastsAndAlerts/constants";

const Label = styled.span`
  color: ${(props) => props.theme?.mortar};
  font-size: 15px;
  font-weight: 600;
`;

const DateSelectContainer = styled.div`
  margin-bottom: 24px;
`;

const DatePickerWrapper = styled.div`
  div.betweenDays.react-datepicker__day--disabled
    ~ div:not(.betweenDays):not(.react-datepicker__day--disabled):not(:hover) {
    background-color: ${(props) => props?.theme?.lightPurple};
    border-radius: 0.3rem;
  }

  div.betweenDays.react-datepicker__day--disabled:not(
      .react-datepicker__day--selected
    ) {
    background-color: ${(props) => props?.theme?.lightPurple};
    border-radius: 0.3rem;
  }

  div[class~="react-datepicker__day--in-selecting-range"]:not(
      .react-datepicker__day--selected
    ) {
    background-color: ${(props) => props?.theme?.lightPurple};
    border-radius: 0.3rem;
  }

  .react-datepicker__day--range-start {
    background-color: ${(props) => props?.theme?.darkPurple};
  }

  div.react-datepicker__day--in-selecting-range:hover {
    background-color: ${(props) => props?.theme?.darkPurple};
  }

  .react-datepicker-popper {
    position: relative !important;
    transform: none !important;
    width: 240px;
    height: 490px;
  }

  @media (max-width: 320px) {
    .react-datepicker__input-container div {
      padding-left: 1.8rem;
      padding-right: 1.8rem;
    }

    .react-datepicker__input-container div i:first-child {
      left: 5px;
    }
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  @media (max-width: 576px) {
    padding-top: 2rem;
    margin-top: auto;
    .btn {
      width: 100%;
    }
  }
`;

const StyledError = styled.div`
  font-weight: 600;
  font-size: 12px;
  color: ${(props) => props.theme?.cardinal};
  text-transform: uppercase;
`;

const ReportGeneratorOptions = (props) => {
  const { fetchEntriesData, fetchDailyTargetHistory, participant } = props;

  const { t } = useTranslation();

  const [selectedReportType, setSelectedReportType] = useState(null);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showErrorDate, setShowErrorDate] = useState(false);
  const [isValidDate, setIsValidDate] = useState(true);
  const [errors, setErrors] = useState({});
  const [isGenerateReportSuccess, setGenerateReportSuccess] = useState(false);
  const [isGenerateReportError, setGenerateReportError] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);
  const [dateRangeLimit, setDateRangeLimit] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);

  const btnGenerateReportErrorMngmt = {
    isSuccess: isGenerateReportSuccess,
    setIsSuccess: setGenerateReportSuccess,
    isError: isGenerateReportError,
    setIsError: setGenerateReportError,
  };

  useEffect(() => {
    const sevenDaysAgo = getPastDate(
      REPORT_TYPES.PDF_VERIFICATION_TOLERATED_DOSE_STUDY_DRUG.minRangeDays
    );
    const consentDate = moment(participant.dateOfEnrollment).startOf("day");
    if (
      selectedReportType?.id !==
      REPORT_TYPES.PDF_VERIFICATION_TOLERATED_DOSE_STUDY_DRUG.id
    ) {
      setAlertMessage(null);
    } else if (consentDate.isAfter(sevenDaysAgo)) {
      setAlertMessage({
        message: "Participant does not have a minimum of 7 days range",
        type: AlertType.DANGER,
        hideDateInput: true,
      });
    } else {
      setAlertMessage({
        message: "Select a period of 7 days.",
        type: AlertType.WARNING,
      });
    }
  }, [selectedReportType]);

  const isDateRangeValid = useMemo(() => {
    return startDate !== "" && endDate !== "";
  }, [startDate, endDate]);

  const isReportTypeValid = useMemo(() => {
    return !!selectedReportType;
  }, [selectedReportType]);

  const generateReport = () => {
    validateReportType();
    validateDateRange();

    if (isDateRangeValid) {
      switch (selectedReportType?.id) {
        case REPORT_TYPES.CSV_24_HOUR.id:
          getCSVReport(
            REPORT_TYPES.CSV_24_HOUR.name,
            "24H_",
            headers24hRecall,
            (reportData) => getDataTo24HRecallReport(reportData.entries)
          );
          break;
        case REPORT_TYPES.PDF_VERIFICATION_TOLERATED_DOSE_STUDY_DRUG.id:
          generatePDF(
            ToleratedDoseStudyDrugReport,
            (reportData, allDates) =>
              getDataToToleratedDoseStudyDrugReport(
                reportData.entries,
                allDates
              ),
            REPORT_TYPES.PDF_VERIFICATION_TOLERATED_DOSE_STUDY_DRUG.reportName
          );
          break;
        case REPORT_TYPES.PDF_DAILY_AVERAGES.id:
          generatePDF(
            DailyAveragesReport,
            (reportData, allDates) =>
              getDataToDailyAveragesReport(reportData.entries, allDates),
            REPORT_TYPES.PDF_DAILY_AVERAGES.reportName
          );
          break;
        case REPORT_TYPES.PDF_STUDY_DRUG_INTAKE.id:
          generatePDF(
            StudyDrugIntakeReport,
            (reportData, allDates) =>
              getDataToStudyDrugIntakeReport(reportData.entries, allDates),
            REPORT_TYPES.PDF_STUDY_DRUG_INTAKE.reportName
          );
          break;
        case REPORT_TYPES.CSV_DIET_RECORD_SUMMARY.id:
          getCSVReport(
            REPORT_TYPES.CSV_DIET_RECORD_SUMMARY.name,
            "DietRecordSummary_",
            headersDietRecordSummary,
            (reportData) => getDataToDietRecordSummaryReport(reportData.entries)
          );
          break;
        default:
          break;
      }
    } else {
      setGenerateReportError(true);
    }
  };

  const getCSVReport = (
    reportName,
    fileNamePrefix,
    headers,
    getDataToReport
  ) => {
    const start = moment(startDate)
      .startOf("day")
      .format(DateFormat.START_DATE);
    const end = moment(endDate).endOf("day").format(DateFormat.END_DATE);

    fetchEntriesData(start, end, participant.participantId).then(
      (reportData) => {
        fetchDailyTargetHistory(reportData.dailyTargetId)
          .then((dailyTargetHistory) => {
            const timestamp = Math.floor(Date.now() / 1000);
            const fileName = `${fileNamePrefix}${participant.userName}_${reportData.generatedDate}_${timestamp}`;
            exportCSVFile(
              headers,
              getDataToReport(reportData),
              parseDailyTargetHistoryData(dailyTargetHistory),
              reportData.generatedDate,
              participant.userName,
              appStudy,
              fileName,
              reportName
            )
              .then(() => {
                setGenerateReportSuccess(true);
              })
              .catch(() => {
                setGenerateReportError(true);
              });
          })
          .catch(() => {
            setGenerateReportError(true);
          });
      }
    );
  };

  const generatePDF = (ReportComponent, getDataToReport, reportName) => {
    const start = moment(startDate)
      .startOf("day")
      .format(DateFormat.START_DATE);
    const end = moment(endDate).endOf("day").format(DateFormat.END_DATE);

    fetchEntriesData(start, end, participant.participantId)
      .then((reportData) => {
        const allDates = getDates(new Date(startDate), new Date(endDate));
        const data = {};
        const totals = getDataToReport(reportData, allDates);
        data.totalPerDay = totals.dateRangeData;
        data.totalsData = totals.totalsData;

        data.generatedDate = reportData.generatedDate;
        data.participantID = participant.userName;
        const dateRange = `${moment(start).format(
          getBaseDateFormat()
        )} to ${moment(end).format(getBaseDateFormat())}`;
        data.dateRange = dateRange;
        data.appStudy = appStudy;

        const timestamp = Math.floor(Date.now() / 1000);

        pdfDownload(
          <ReportComponent {...data} />,
          `${data.participantID}_${reportName}_${data.generatedDate}_${timestamp}`
        )
          .then(() => {
            setGenerateReportSuccess(true);
          })
          .catch(() => {
            setGenerateReportError(true);
          });
      })
      .catch(() => {
        setGenerateReportError(true);
      });
  };

  const handleReportTypeOnChange = (value) => {
    if (value) {
      setSelectedReportType(value);
    }

    if (
      selectedReportType?.id ===
      REPORT_TYPES.PDF_VERIFICATION_TOLERATED_DOSE_STUDY_DRUG.id
    ) {
      setDateRangeLimit(true);
    } else {
      setDateRangeLimit(false);
    }

    setStartDate("");
    setEndDate("");
    setAvailableDates([]);
    setErrors(null);
    setGenerateReportError(true);
  };

  const errorHandling = {
    showErrorDate: showErrorDate,
    setShowErrorDate: setShowErrorDate,
    isValidDate: isValidDate,
    setIsValidDate: setIsValidDate,
  };

  const generateDates = (startDate) => {
    const weeks = 1;
    const weeksDays = 6;
    const date = new Date(startDate.getTime());
    const limitDate = date.setDate(
      new Date(date).getDate() + weeks * weeksDays + 1
    );
    return generateWeeks(startDate, limitDate);
  };

  const onDateChange = (dates) => {
    const [start, end] = dates;

    if (start && !end) {
      const maxWeeks = generateDates(start);
      setAvailableDates(maxWeeks);
    }

    if (start && end) {
      setStartDate(start);
      setEndDate(end);
    }
  };

  //Check if the provided date is the last day of the week
  const filterDates = (date) => {
    if (availableDates.length > 0) {
      return availableDates
        .map((availableDate) => availableDate[1])
        .includes(moment(date).format(DateFormat.INVERTED_DATE));
    }
    return true;
  };

  //Reset calendar state
  const onCalendarClose = () => {
    if (startDate && !endDate) {
      onDateChange([startDate, startDate]);
    }
  };

  const validateReportType = useCallback(
    (value) => {
      if (!isReportTypeValid) {
        setGenerateReportError(true);
      }
      setErrors((prevState) => ({
        ...prevState,
        reportType: isReportTypeValid
          ? undefined
          : "Please select a report type.",
      }));
    },
    [isReportTypeValid]
  );

  const getErrorMsg = useMemo(() => {
    return "Please select a date range";
  }, []);

  const disableGenerateBtn = useMemo(() => {
    return (
      selectedReportType?.id ===
        REPORT_TYPES.PDF_VERIFICATION_TOLERATED_DOSE_STUDY_DRUG.id &&
      startDate &&
      startDate === endDate
    );
  }, [selectedReportType, startDate, endDate]);

  const validateDateRange = useCallback(
    (value) => {
      setErrors((prevState) => ({
        ...prevState,
        dateRange: isDateRangeValid ? undefined : getErrorMsg,
      }));
    },
    [getErrorMsg, isDateRangeValid]
  );

  const maxDate = useMemo(() => {
    return selectedReportType?.id ===
      REPORT_TYPES.PDF_VERIFICATION_TOLERATED_DOSE_STUDY_DRUG.id &&
      startDate === ""
      ? moment().subtract(6, "days").toDate()
      : new Date();
  }, [selectedReportType, startDate]);

  return (
    <div data-testid="nutrition-report">
      <div className="mb-4">
        <Label className="mb-1">Select report type:</Label>
        <CustomDropdown
          placeholder={t("appText.select")}
          options={Object.values(REPORT_TYPES)}
          value={selectedReportType?.value}
          onChange={(e) => handleReportTypeOnChange(e)}
          width={200}
        />
        <StyledError className="mt-2">
          {errors?.reportType && <span>{errors?.reportType}</span>}
        </StyledError>
      </div>

      {selectedReportType && (
        <>
          {!alertMessage?.hideDateInput && (
            <DateSelectContainer>
              <Label htmlFor="dateselect" className="mb-1">
                Date range:
              </Label>
              <DatePickerWrapper>
                <DateSelect
                  data-testid="sted"
                  eventDate={startDate}
                  setEventDate={setStartDate}
                  startDate={startDate}
                  setStartDate={setStartDate}
                  endDate={endDate}
                  setEndDate={setEndDate}
                  errorHandling={errorHandling}
                  monthsShown={2}
                  selectsRange={true}
                  displayTitle={false}
                  maxDate={maxDate}
                  minDate={moment(participant.dateOfEnrollment).toDate()}
                  onCalendarClose={onCalendarClose}
                  onChange={onDateChange}
                  filterDate={dateRangeLimit ? filterDates : undefined}
                  onCalendarOpen={() => handleReportTypeOnChange(null)}
                />
              </DatePickerWrapper>
              <StyledError>
                {errors?.dateRange && <span>{errors?.dateRange}</span>}
              </StyledError>
            </DateSelectContainer>
          )}
          {alertMessage?.message && (
            <AlertUGNX type={alertMessage?.type}>
              {alertMessage?.message}
            </AlertUGNX>
          )}
        </>
      )}
      <ActionButtonsContainer>
        <AnimatedButton
          data-testid="generate-report"
          onClick={generateReport}
          disabled={!!disableGenerateBtn || !!alertMessage?.hideDateInput}
          icon={null}
          {...btnGenerateReportErrorMngmt}
        >
          Generate Report
        </AnimatedButton>
      </ActionButtonsContainer>
    </div>
  );
};

export default withTheme(ReportGeneratorOptions);
